const loadMeasurementVue = () => {
  if (window.Vue) return Promise.resolve(window.Vue)
  if (window.measurementVuePromise) return window.measurementVuePromise

  window.measurementVuePromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://unpkg.com/vue@3.5.39/dist/vue.global.prod.js'
    script.async = true
    script.onload = () => resolve(window.Vue)
    script.onerror = () => reject(new Error('Unable to load the measurement tool interface.'))
    document.head.appendChild(script)
  })

  return window.measurementVuePromise
}

loadMeasurementVue().then(({ createApp }) => {

window.measurementApp = createApp({
  data() {
    // Load saved data from localStorage
    const savedData = this.loadFromMemory()
    
    return {
      currentStep: 1,
      showWidthExtensions: false,
      showHeightFrameToFloor: false,
      hasShownCelebration: false,
      roomName: savedData.roomName || '',
      measurements: {
        headerStyle: savedData.measurements?.headerStyle || '',
        hasRod: savedData.measurements?.hasRod ?? null,
        rodWidth: savedData.measurements?.rodWidth || '',
        windowWidth: savedData.measurements?.windowWidth || '',
        rodLeft: savedData.measurements?.rodLeft || '',
        rodRight: savedData.measurements?.rodRight || '',
        heightRodToFloor: savedData.measurements?.heightRodToFloor || '',
        heightRodToFrame: savedData.measurements?.heightRodToFrame || '',
        heightFrameToFloor: savedData.measurements?.heightFrameToFloor || '',
        panelType: savedData.measurements?.panelType || 'Split',
        fullness: savedData.measurements?.fullness || '',
        bottomType: savedData.measurements?.bottomType || 'kiss'
      },
      steps: [
        { name: 'Header' },
        { name: 'Rod' },
        { name: 'Width' },
        { name: 'Height' },
        { name: 'Panels' },
        { name: 'Fullness' },
        { name: 'Bottom' },
        { name: 'Summary' }
      ],
      headerOptions: [
        {
          value: 'grommet',
          title: 'Grommet Top',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Grommet_Top.webp',
          fullnessOptions: [1.5, 2.0, 2.5],
          description: 'Modern and versatile design with metal rings that slide easily on the rod. Perfect for contemporary spaces.',
          features: ['Easy to open and close', 'Modern aesthetic', 'Durable metal grommets', 'Works with standard rods']
        },
        {
          value: 'rod_pocket',
          title: 'Rod Pocket',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Rod_Pocket.webp',
          fullnessOptions: [1.0, 1.5, 2.0, 2.5],
          description: 'Classic and elegant style with a sewn-in pocket that slides onto the curtain rod. Creates beautiful, gathered folds.',
          features: ['Traditional look', 'Soft gathered appearance', 'Hidden rod pocket', 'Versatile fullness options']
        },
        {
          value: 'pinch_pleat_double',
          title: 'Pinch Pleat (Double)',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Pinch_Pleat_-_Double.webp',
          fullnessOptions: [2.0],
          description: 'Sophisticated tailored pleats with two folds pinched together. Offers a formal, polished appearance.',
          features: ['Formal elegance', 'Structured pleats', 'Classic design', 'Professional finish']
        },
        {
          value: 'pinch_pleat_triple',
          title: 'Pinch Pleat (Triple)',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Pinch_Pleat_-_Triple.webp',
          fullnessOptions: [2.5],
          description: 'Luxurious three-fold pleats that create dramatic depth and texture. The most traditional and formal option.',
          features: ['Maximum luxury', 'Deep pleated folds', 'Traditional elegance', 'Rich fullness']
        },
        {
          value: 'euro_pleat_double',
          title: 'Euro Pleat (Double)',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Euro_Pleat_-_Double.webp',
          fullnessOptions: [2.0],
          description: 'European-style pleating with a sleek, cylindrical shape. Combines modern simplicity with classic elegance.',
          features: ['Contemporary European style', 'Smooth cylindrical pleats', 'Refined appearance', 'Architectural look']
        },
        {
          value: 'euro_pleat_triple',
          title: 'Euro Pleat (Triple)',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Euro_Pleat_-_Triple.webp',
          fullnessOptions: [2.5],
          description: 'Enhanced euro pleating with fuller, more dimensional folds. Perfect for larger windows and grand spaces.',
          features: ['Enhanced fullness', 'Dimensional design', 'Luxury aesthetic', 'Statement piece']
        },
        {
          value: 'tab_top',
          title: 'Tab Top',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Tab_Top.webp',
          fullnessOptions: [1.0, 1.5, 2.0],
          description: 'Casual and charming with fabric loops that attach directly to the rod. Great for relaxed, informal settings.',
          features: ['Casual aesthetic', 'Easy to install', 'Decorative tabs', 'Perfect for informal spaces']
        },
        {
          value: 'flat_panel',
          title: 'Flat Panel',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Flat_Panel.webp',
          fullnessOptions: [1.0, 1.5, 2.0],
          description: 'Minimalist and contemporary with clean lines and no pleating. Ideal for modern, sleek interiors.',
          features: ['Ultra-modern look', 'Clean lines', 'Minimal fullness', 'Scandinavian style']
        },
        {
          value: 'ripple_fold',
          title: 'Ripple Fold',
          image: 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_header_Ripple_Fold.webp',
          fullnessOptions: [2.0],
          description: 'Continuous S-curve waves creating a flowing, uniform appearance. The most contemporary and architectural style.',
          features: ['Flowing waves', 'Contemporary elegance', 'Uniform appearance', 'Track system required']
        }
      ],
      bottomOptions: [
        {
          value: 'half',
          title: '1" Above Floor',
          desc: 'Recommended choice',
          offset: -1
        },
        {
          value: 'kiss',
          title: 'Touch the floor',
          desc: 'Tailored choice',
          offset: 0
        },
        {
          value: 'puddle',
          title: '2" Puddle On Floor',
          desc: 'Luxury choice',
          offset: 2
        }
      ]
    }
  },
  computed: {
    availableFullnessOptions() {
      if (!this.measurements.headerStyle) {
        return []
      }
      const header = this.headerOptions.find(
        h => h.value === this.measurements.headerStyle
      )
      if (!header) return []

      return header.fullnessOptions.map(value => {
        const labels = {
          1.0: { title: '1.0x Fullness', desc: 'Minimum choice' },
          1.5: { title: '1.5x Fullness', desc: 'Budget choice' },
          2.0: { title: '2.0x Fullness', desc: 'Recommended choice' },
          2.5: { title: '2.5x Fullness', desc: 'Luxury choice' }
        }
        return { value, ...labels[value] }
      })
    },
    maxAccessibleStep() {
      let maxStep = 1

      if (this.measurements.headerStyle) maxStep = 2
      if (this.measurements.hasRod !== null) maxStep = 3

      if (
        this.measurements.hasRod === true &&
        this.measurements.rodWidth
      ) {
        maxStep = 4
      } else if (
        this.measurements.hasRod === false &&
        this.measurements.windowWidth &&
        this.measurements.rodLeft &&
        this.measurements.rodRight
      ) {
        maxStep = 4
      }

      if (
        this.measurements.hasRod === true &&
        this.measurements.heightRodToFloor
      ) {
        maxStep = 5
      } else if (
        this.measurements.hasRod === false &&
        this.measurements.heightRodToFrame &&
        this.measurements.heightFrameToFloor
      ) {
        maxStep = 5
      }

      if (this.measurements.panelType) maxStep = 6
      if (this.measurements.fullness) maxStep = 7
      if (this.measurements.bottomType) maxStep = 8

      return maxStep
    },
    selectedHeaderInfo() {
      return this.headerOptions.find(
        opt => opt.value === this.measurements.headerStyle
      ) || null
    }
  },
  methods: {
    loadFromMemory() {
      try {
        const saved = localStorage.getItem('curtainMeasurementDraft')
        if (saved) {
          const data = JSON.parse(saved)
          // If rawMeasurements exists, use it (it has the original headerStyle value)
          if (data.rawMeasurements) {
            return {
              roomName: data.roomName || '',
              measurements: data.rawMeasurements
            }
          }
          // Fallback to old structure
          return data
        }
        return { roomName: '', measurements: {} }
      } catch (e) {
        console.error('Error loading draft:', e)
        return { roomName: '', measurements: {} }
      }
    },
    saveToMemory() {
      try {
        const data = {
          roomName: this.roomName,
          measurements: this.measurements,
          timestamp: new Date().toISOString()
        }
        localStorage.setItem('curtainMeasurementDraft', JSON.stringify(data))
      } catch (e) {
        console.error('Error saving draft:', e)
      }
    },
    getBottomFilenameSuffix(headerStyle, fullness, bottomType) {
      // 所有图片文件名已统一使用 puddle
      return bottomType
    },
    selectHeaderStyle(value) {
      console.log('Selecting header style:', value, 'Previous:', this.measurements.headerStyle)
      this.measurements.headerStyle = value
      console.log('After selection:', this.measurements.headerStyle)
      let header = this.headerOptions.find(
        h => h.value === this.measurements.headerStyle
      )
     
        this.measurements.fullness = header.fullnessOptions[0]
      
      this.saveToMemory()
    },
    selectRodStatus(hasRod) {
      this.measurements.hasRod = hasRod
      // Reset substep states when rod status changes
      this.showWidthExtensions = false
      this.showHeightFrameToFloor = false
      this.saveToMemory()
      this.nextStep()
    },
    nextStep() {
      if (this.currentStep < 8) {
        this.currentStep++
        // Reset substep states when moving to next step
        this.showWidthExtensions = false
        this.showHeightFrameToFloor = false
        
        // If we just reached step 8 (Summary), auto-save and show celebration
        if (this.currentStep === 8) {
          this.$nextTick(() => {
            this.autoSaveOnSummary()
          })
        }
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        // If leaving Summary step, reset celebration flag
        if (this.currentStep === 8) {
          this.hasShownCelebration = false
        }
        
        this.currentStep--
        // Reset substep states when going back
        this.showWidthExtensions = false
        this.showHeightFrameToFloor = false
      }
    },
    goToStep(step) {
      // 只允许跳转到已完成的步骤
      if (step <= this.maxAccessibleStep) {
        this.currentStep = step
      }
    },
    proceedToExtensions() {
      // Show the extensions input step
      if (this.measurements.windowWidth) {
        this.showWidthExtensions = true
      }
    },
    proceedToFrameHeight() {
      // Show the frame to floor height input step
      if (this.measurements.heightRodToFrame) {
        this.showHeightFrameToFloor = true
      }
    },
    getHeaderStyleName() {
      const option = this.headerOptions.find(
        opt => opt.value === this.measurements.headerStyle
      )
      return option ? option.title : ''
    },
    getBottomTypeName() {
      const option = this.bottomOptions.find(
        opt => opt.value === this.measurements.bottomType
      )
      return option ? option.title : ''
    },
    getTotalWidth() {
      if (
        this.measurements.hasRod === true &&
        this.measurements.rodWidth
      ) {
        return this.measurements.rodWidth
      } else if (
        this.measurements.windowWidth &&
        this.measurements.rodLeft &&
        this.measurements.rodRight
      ) {
        return (
          parseFloat(this.measurements.windowWidth) +
          parseFloat(this.measurements.rodLeft) +
          parseFloat(this.measurements.rodRight)
        )
      }
      return this.measurements.windowWidth || '0'
    },
    getTotalHeight() {
      if (this.measurements.hasRod === true) {
        return this.measurements.heightRodToFloor || '0'
      } else {
        const rodToFrame =
          parseFloat(this.measurements.heightRodToFrame) || 0
        const frameToFloor =
          parseFloat(this.measurements.heightFrameToFloor) || 0
        return rodToFrame + frameToFloor
      }
    },
    // Dynamic image methods based on header style
    getPanelPreviewImage(panelType) {
      // 为 Step 5 的选项生成预览图
      if (!this.measurements.headerStyle) {
        return `https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_${panelType}_grommet_${panelType}_2.0_kiss.webp`
      }

      // 获取当前 header 的默认或已选择的 fullness
      const header = this.headerOptions.find(
        h => h.value === this.measurements.headerStyle
      )
      let fullness = this.measurements.fullness

      // 如果还没选择 fullness，使用该 header 支持的第一个 fullness 值
      if (!fullness && header) {
        fullness = header.fullnessOptions[0]
      }
      
      const fullnessStr = (fullness || 2.0).toFixed(1)
      return `https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_${panelType}_${this.measurements.headerStyle}_${panelType}_${fullnessStr}_kiss.webp`
    },
    getFullnessPreviewImage(fullness) {
      // 为 Step 6 的选项生成预览图
      const header = this.measurements.headerStyle || 'grommet'
      const panel = (
        this.measurements.panelType || 'single'
      ).toLowerCase()
      const fullnessStr = fullness.toFixed(1)
      return `https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_${panel}_${header}_${panel}_${fullnessStr}_kiss.webp`
    },
    getBottomPreviewImage(bottomType) {
      // 为 Step 7 的选项生成预览图
      const header = this.measurements.headerStyle || 'grommet'
      const panel = (
        this.measurements.panelType || 'single'
      ).toLowerCase()
      const fullness = (this.measurements.fullness || 2.0).toFixed(1)
      const bottomSuffix = this.getBottomFilenameSuffix(
        header,
        parseFloat(fullness),
        bottomType
      )
      return `https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_${panel}_${header}_${panel}_${fullness}_${bottomSuffix}.webp`
    },
    getWidthImage() {
      if (this.measurements.hasRod === true) {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_window_width_with_arrow_rod.webp'
      } else if (!this.showWidthExtensions) {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_window_width_with_arrow_no_rod.webp'
      } else {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_window_width_beyond.webp'
      }
    },
    getHeightImage() {
      if (this.measurements.hasRod === true) {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_window_height_with_arrow_rod.webp'
      } else if (!this.showHeightFrameToFloor) {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_window_height_with_arrow_no_rod.webp'
      } else {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_window_height_between.webp'
      }
    },
    getPanelImage() {
      // 根据当前选择的 header, panel, fullness 和 bottom 类型构建图片路径
      if (
        !this.measurements.headerStyle ||
        !this.measurements.panelType
      ) {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_single_grommet_single_2.0_kiss.webp'
      }
       // 获取当前 header 的默认或已选择的 fullness
      const header = this.headerOptions.find(
        h => h.value === this.measurements.headerStyle
      )
      let fullness = this.measurements.fullness

      // 如果还没选择 fullness，使用该 header 支持的第一个 fullness 值
      if (!fullness && header) {
        fullness = header.fullnessOptions[0]
      }

      const panelType = this.measurements.panelType.toLowerCase()
      fullness = (this.measurements.fullness || 2.0).toFixed(1)
      const bottom = 'kiss' // 默认显示 kiss
      console.log(this.measurements,3)
      return `https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_${panelType}_${this.measurements.headerStyle}_${panelType}_${fullness}_${bottom}.webp`
    },
    getFullnessImage() {
      // Fullness 步骤显示带有当前 fullness 的图片
      if (
        !this.measurements.headerStyle ||
        !this.measurements.panelType ||
        !this.measurements.fullness
      ) {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_single_grommet_single_2.0_kiss.webp'
      }

      const panelType = this.measurements.panelType.toLowerCase()
      const fullness = this.measurements.fullness.toFixed(1)
      const bottom = 'kiss'

      return `https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_${panelType}_${this.measurements.headerStyle}_${panelType}_${fullness}_${bottom}.webp`
    },
    getBottomImage() {
      // Bottom 步骤显示带有当前 bottom 类型的图片
      if (
        !this.measurements.headerStyle ||
        !this.measurements.panelType ||
        !this.measurements.fullness
      ) {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_single_grommet_single_2.0_kiss.webp'
      }

      const panelType = this.measurements.panelType.toLowerCase()
      const fullness = this.measurements.fullness.toFixed(1)
      const bottomRaw = this.measurements.bottomType || 'kiss'
      const bottom = this.getBottomFilenameSuffix(
        this.measurements.headerStyle,
        fullness,
        bottomRaw
      )

      return `https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_${panelType}_${this.measurements.headerStyle}_${panelType}_${fullness}_${bottom}.webp`
    },
    getSummaryImage() {
      // Summary 显示最终完整配置的图片
      if (
        !this.measurements.headerStyle ||
        !this.measurements.panelType ||
        !this.measurements.fullness ||
        !this.measurements.bottomType
      ) {
        return 'https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_single_grommet_single_2.0_kiss.webp'
      }

      const panelType = this.measurements.panelType.toLowerCase()
      const fullness = this.measurements.fullness.toFixed(1)
      const bottomRaw = this.measurements.bottomType
      const bottom = this.getBottomFilenameSuffix(
        this.measurements.headerStyle,
        fullness,
        bottomRaw
      )

      return `https://cdn.shopify.com/s/files/1/0684/4596/2321/files/massets_${panelType}_${this.measurements.headerStyle}_${panelType}_${fullness}_${bottom}.webp`
    },
    getPanelWidth() {
      const totalWidth = parseFloat(this.getTotalWidth())
      const fullness = parseFloat(this.measurements.fullness) || 2.0
      const width = totalWidth * fullness
      
      if (this.measurements.panelType === 'Split') {
        return Math.round(width / 2)
      } else if (this.measurements.panelType === 'Single') {
        return Math.round(width)
      }
      return Math.round(width)
    },
    getFinalHeight() {
      const totalHeight = parseFloat(this.getTotalHeight())
      const bottomOption = this.bottomOptions.find(
        opt => opt.value === this.measurements.bottomType
      )
      const offset = bottomOption ? bottomOption.offset : 0
      return Math.round(totalHeight + offset)
    },
    getPanelQuantity() {
      if (this.measurements.panelType === 'Split') {
        return '2 Panels'
      } else if (this.measurements.panelType === 'Single') {
        return '1 Panel'
      }
      return '1 Panel'
    },
    saveMeasurement() {
      const measurementData = {
        roomName: this.roomName || 'Untitled',
        headerStyle: this.getHeaderStyleName(),
        width: this.getPanelWidth(),
        height: this.getFinalHeight(),
        quantity: this.getPanelQuantity(),
        panelType: this.measurements.panelType,
        fullness: this.measurements.fullness,
        timestamp: new Date().toISOString(),
        rawMeasurements: { ...this.measurements }
      }

      // Get existing measurements from localStorage
      let savedMeasurements = []
      try {
        const existing = localStorage.getItem('curtainMeasurements')
        if (existing) {
          savedMeasurements = JSON.parse(existing)
        }
      } catch (e) {
        console.error('Error reading saved measurements:', e)
      }

      // Add new measurement
      savedMeasurements.push(measurementData)

      // Save back to localStorage
      try {
        localStorage.setItem('curtainMeasurements', JSON.stringify(savedMeasurements))
        
        // Show confetti and success message
        this.showConfetti()
        
        // Show success overlay
        const overlay = document.getElementById('success-overlay')
        if (overlay) {
          overlay.style.display = 'block'
          overlay.style.opacity = '0'
          overlay.style.transition = 'opacity 0.3s ease'
          
          // Fade in
          setTimeout(() => {
            overlay.style.opacity = '1'
          }, 10)
          
          // Fade out after 2.5 seconds
          setTimeout(() => {
            overlay.style.opacity = '0'
            setTimeout(() => {
              overlay.style.display = 'none'
            }, 300)
          }, 2500)
        }
        
        // Clear draft after saving
        localStorage.removeItem('curtainMeasurementDraft')
      } catch (e) {
        console.error('Error saving measurement:', e)
        alert('Failed to save measurement. Please try again.')
      }
    },
    showConfetti() {
      const canvas = document.getElementById('confetti-canvas')
      if (!canvas) return
      
      const ctx = canvas.getContext('2d')
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      
      const confetti = []
      const confettiCount = 150
      const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffa500', '#ff69b4']
      
      // Create confetti particles
      for (let i = 0; i < confettiCount; i++) {
        confetti.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height - canvas.height,
          r: Math.random() * 6 + 4,
          d: Math.random() * confettiCount,
          color: colors[Math.floor(Math.random() * colors.length)],
          tilt: Math.floor(Math.random() * 10) - 10,
          tiltAngleIncremental: Math.random() * 0.07 + 0.05,
          tiltAngle: 0
        })
      }
      
      let animationFrame
      function draw() {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        
        confetti.forEach((c, index) => {
          ctx.beginPath()
          ctx.lineWidth = c.r / 2
          ctx.strokeStyle = c.color
          ctx.moveTo(c.x + c.tilt + c.r / 4, c.y)
          ctx.lineTo(c.x + c.tilt, c.y + c.tilt + c.r / 4)
          ctx.stroke()
          
          c.tiltAngle += c.tiltAngleIncremental
          c.y += (Math.cos(c.d) + 3 + c.r / 2) / 2
          c.x += Math.sin(c.d)
          c.tilt = Math.sin(c.tiltAngle - index / 3) * 15
          
          if (c.y > canvas.height) {
            confetti[index] = {
              x: Math.random() * canvas.width,
              y: -10,
              r: c.r,
              d: c.d,
              color: c.color,
              tilt: c.tilt,
              tiltAngleIncremental: c.tiltAngleIncremental,
              tiltAngle: c.tiltAngle
            }
          }
        })
        
        animationFrame = requestAnimationFrame(draw)
      }
      
      draw()
      
      // Stop animation after 3 seconds
      setTimeout(() => {
        cancelAnimationFrame(animationFrame)
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }, 3000)
    },
    closeTool() {
      // Go back to previous page if history exists, otherwise go to homepage
      if (window.history.length > 1) {
        window.history.back()
      } else {
        window.location.href = '/'
      }
    },
    goToShop() {
      // Navigate to collections page
      window.location.href = '/collections/all'
    },
    autoSaveOnSummary() {
      // Check if we've already shown celebration for this session
      if (this.hasShownCelebration) {
        return
      }
      
      // First time reaching summary, just celebrate! (don't save yet)
      this.showConfetti()
      
      // Show success overlay
      const overlay = document.getElementById('success-overlay')
      if (overlay) {
        overlay.style.display = 'block'
        overlay.style.opacity = '0'
        overlay.style.transition = 'opacity 0.3s ease'
        
        // Fade in
        setTimeout(() => {
          overlay.style.opacity = '1'
        }, 10)
        
        // Fade out after 2.5 seconds
        setTimeout(() => {
          overlay.style.opacity = '0'
          setTimeout(() => {
            overlay.style.display = 'none'
          }, 300)
        }, 2500)
      }
      
      // Mark that we've shown the celebration
      this.hasShownCelebration = true
    },
    handleShopNow() {
      // Save the measurement and navigate to shop
      this.saveMeasurementWithoutCelebration()
      this.goToShop()
    },
    saveMeasurementWithoutCelebration() {
      const measurementData = {
        roomName: this.roomName || 'Untitled',
        headerStyle: this.getHeaderStyleName(),
        width: this.getPanelWidth(),
        height: this.getFinalHeight(),
        quantity: this.getPanelQuantity(),
        panelType: this.measurements.panelType,
        fullness: this.measurements.fullness,
        timestamp: new Date().toISOString(),
        rawMeasurements: { ...this.measurements }
      }

      // Get existing measurements from localStorage
      let savedMeasurements = []
      try {
        const existing = localStorage.getItem('curtainMeasurements')
        if (existing) {
          savedMeasurements = JSON.parse(existing)
        }
      } catch (e) {
        console.error('Error reading saved measurements:', e)
      }

      // Check if already exists (avoid duplicates)
      const alreadyExists = savedMeasurements.some(m => 
        m.headerStyle === measurementData.headerStyle &&
        m.width === measurementData.width &&
        m.height === measurementData.height &&
        m.roomName === measurementData.roomName
      )

      if (!alreadyExists) {
        // Add new measurement
        savedMeasurements.push(measurementData)

        // Save back to localStorage
        try {
          localStorage.setItem('curtainMeasurements', JSON.stringify(savedMeasurements))
          
          // Clear draft after saving
          localStorage.removeItem('curtainMeasurementDraft')
        } catch (e) {
          console.error('Error saving measurement:', e)
        }
      }
    },
    
    loadDraft() {
      try {
        const draft = localStorage.getItem('curtainMeasurementDraft')
        if (draft) {
          const data = JSON.parse(draft)
          this.roomName = data.roomName || ''
          
          // If rawMeasurements exists, use it (it has the original headerStyle value)
          if (data.rawMeasurements) {
            Object.assign(this.measurements, data.rawMeasurements)
          } else if (data.measurements) {
            // Fallback to old structure
            Object.assign(this.measurements, data.measurements)
          }
          
          console.log('Draft loaded:', data)
          console.log('Loaded headerStyle:', this.measurements.headerStyle)
          return true
        }
        return false
      } catch (e) {
        console.error('Error loading draft:', e)
        return false
      }
    }
  },
  mounted() {
    // Check if there's a draft to load when page first loads
    const hasDraft = localStorage.getItem('curtainMeasurementDraft')
    if (hasDraft) {
      try {
        const data = JSON.parse(hasDraft)
        if (data.roomName) {
          console.log('Editing measurement:', data.roomName)
          // Show a toast notification
          this.showEditingNotification(data.roomName)
        }
      } catch (e) {
        console.error('Error checking draft:', e)
      }
    }
  }
}).mount('#app')

// Add a helper function to show editing notification
if (window.measurementApp && window.measurementApp.showEditingNotification === undefined) {
  window.measurementApp.showEditingNotification = function(roomName) {
    const toast = document.createElement('div')
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #0891b2;
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
      animation: slideIn 0.3s ease;
    `
    toast.innerHTML = `✏️ Editing: <strong>${roomName}</strong>`
    
    document.body.appendChild(toast)
    
    setTimeout(() => {
      toast.style.animation = 'slideIn 0.3s ease reverse'
      setTimeout(() => {
        if (toast.parentNode) {
          document.body.removeChild(toast)
        }
      }, 300)
    }, 3000)
  }
}
}).catch((error) => {
  console.error('Unable to initialize the measurement tool:', error)
})
